import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  ductedKeptComponentIds,
  resultSetsFor
} from 'features/ducted-compatibility';
import { setDuctedApplySizingBands } from 'features/ui';
import { useCallback, useMemo } from 'react';
import { getChooseSystemsEligibleDuctedSystems } from 'utils/existing-equipment';

// Sizing-bands banner for Choose Systems (AHA-6796 bands T3): surfaces the
// server-side band constraint when it actually excluded rows and offers the
// one-click relax, mirroring TonnageFilterBanner's two-state pattern
// (active banner / dismissed re-apply strip). Renders nothing when bands
// never bit (gate off, or the constraint excluded nothing) — the gate-off
// no-op: the server never sends sizingApplied, so state 1 is unreachable
// and the override is never set. Settled relaxed entries echo
// sizingApplied: false, so the active state also accepts relaxed-provenance
// data (sizingRelaxed) — that keeps the banner up optimistically through a
// re-apply click until the banded refetch settles to the server echo.
// Error-settled entries are carved out: a failed banded refetch retains the
// relaxed data under status 'error', and the banner must not claim sized
// results over those all-sizes rows.
export default function SizingBandsBanner() {
  const dispatch = useAppDispatch();
  const applySizingBands = useAppSelector(
    (state) => state.ui.ductedFilters.applySizingBands
  );
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  const wantCompositionPicksBySystem = useAppSelector(
    (state) => state.structure.wantCompositionPicksBySystem
  );
  const resultsBySystem = useAppSelector(
    (state) => state.ductedCompatibility.resultsBySystem
  );

  // One pass over the (eligible system x in-play result set) entries — kept
  // OR alternatives — for the two active-banner signals. anySizingApplied:
  // the server's reduction report (zero-results sets count too — pnet echoes
  // true whenever the constraint excluded rows, including down to zero, the
  // D4 escape). anyRelaxedData: relaxed-provenance data is still on screen.
  // Settled relaxed entries echo sizingApplied: false and are retained
  // (still relaxed) through the re-apply click and the banded refetch's
  // loading window, so the provenance disjunct is what keeps the re-apply
  // transition flicker-free; the optimistic banner resolves to the honest
  // server echo once the banded refetch settles. Error settles don't count:
  // a failed banded refetch retains the relaxed entry under status 'error',
  // and claiming sized results over those all-sizes rows would be false —
  // the banner drops instead. anyNearestFit: the band fit nothing for some
  // set and the server sent the closest sizes — the banner says so instead
  // of claiming the results are sized to the home. It opens the banner on
  // its own: sizingApplied is reduction-based, and when every row misses the
  // band and all of them sit within slack of the closest (a one-match kept
  // set, typically) nothing was excluded, so it reads false while the
  // results are nearest-fit all the same.
  const { anySizingApplied, anyRelaxedData, anyNearestFit } = useMemo(() => {
    let applied = false;
    let relaxed = false;
    let nearestFit = false;
    getChooseSystemsEligibleDuctedSystems(
      existingSystems,
      recommendationSelectionsBySystem,
      wantCompositionPicksBySystem
    ).forEach((system) =>
      resultSetsFor(ductedKeptComponentIds(system)).forEach((resultSet) => {
        const setEntry = resultsBySystem[system.uniqueId]?.[resultSet];
        if (setEntry?.sizingApplied === true) applied = true;
        if (setEntry?.sizingNearestFit === true) nearestFit = true;
        if (setEntry?.sizingRelaxed === true && setEntry.status !== 'error')
          relaxed = true;
      })
    );
    return {
      anySizingApplied: applied,
      anyRelaxedData: relaxed,
      anyNearestFit: nearestFit
    };
  }, [
    existingSystems,
    recommendationSelectionsBySystem,
    wantCompositionPicksBySystem,
    resultsBySystem
  ]);

  const handleShowAllSizes = useCallback(() => {
    dispatch(setDuctedApplySizingBands(false));
  }, [dispatch]);

  const handleReapply = useCallback(() => {
    dispatch(setDuctedApplySizingBands(true));
  }, [dispatch]);

  if (
    applySizingBands &&
    (anySizingApplied || anyNearestFit || anyRelaxedData)
  ) {
    // The nearest-fit copy holds whether every set or only one of them
    // (kept vs alternatives) missed the band: it claims the closest sizes
    // only where nothing fit, not that nothing fit anywhere.
    return (
      <div
        data-testid='sizing-bands-banner'
        className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 ${
          anyNearestFit
            ? 'border-amber-200 bg-amber-50'
            : 'border-blue-100 bg-blue-50'
        }`}
      >
        <span className='text-xs text-gray-600'>
          {anyNearestFit
            ? "Showing the closest available sizes where nothing falls within the recommended size range for your home's requirements."
            : "Showing systems sized to your home's requirements."}
        </span>
        <button
          type='button'
          className='ml-auto text-xs font-medium text-blue-600 underline hover:text-blue-700'
          onClick={handleShowAllSizes}
        >
          Show all sizes
        </button>
      </div>
    );
  }

  // Relaxed state keys on the override flag, not the response — relaxed
  // responses no longer echo sizingApplied: true.
  if (!applySizingBands) {
    return (
      <div
        data-testid='sizing-bands-banner'
        className='flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2'
      >
        <span className='text-xs text-gray-600'>
          Sizing filter off — showing all sizes
        </span>
        <button
          type='button'
          className='ml-auto text-xs font-medium text-blue-600 underline hover:text-blue-700'
          onClick={handleReapply}
        >
          Re-apply
        </button>
      </div>
    );
  }

  return null;
}
